/**
 * Persistenza storage — rischio n.1 del M0 (ricerca build plan §2.2):
 * WebKit di default è BEST-EFFORT ("their persistence is not guaranteed and their data can be
 * evicted", [A] webkit storage policy). Quindi: `persist()` va RICHIESTO e lo stato va VISIBILE
 * in status line (niente magia: l'utente deve poter vedere se i dati sono protetti).
 * `QuotaExceededError` gestito a monte dal writer (db.ts) — mai eccezione nuda.
 */
export interface PersistStatus {
  persisted: boolean;
  quota: number | null;
}

export async function requestPersist(): Promise<boolean> {
  try {
    if (navigator.storage?.persist) return await navigator.storage.persist();
  } catch {
    /* best-effort: mai bloccare l'utente */
  }
  return false;
}

export async function persistStatus(): Promise<PersistStatus> {
  const status: PersistStatus = { persisted: false, quota: null };
  try {
    if (navigator.storage?.persisted) status.persisted = await navigator.storage.persisted();
    // quota = tetto disponibile. L'uso NON si prende da qui: `estimate().usage` su WebKit è una
    // stima grossolana (~1MB per chiamata, ritardata) che include l'intera origine (cache
    // compresa) e cresce a ogni log per poi resettarsi al riavvio — sembrava un leak (bug device
    // 2026-09-25). La verità dei DATI è il walk OPFS qui sotto.
    if (navigator.storage?.estimate) {
      const est = await navigator.storage.estimate();
      status.quota = est.quota ?? null;
    }
  } catch {
    /* status line mostra "?" — mai crash per le stime */
  }
  return status;
}

/**
 * Byte REALI occupati dai dati: walk OPFS ricorsivo, file per file. Deterministico e
 * ispezionabile (regola 1): questo è il numero che l'utente deve vedere, non la stima piattaforma.
 * Su engine senza OPFS/iterazione ritorna null (status line dice "?", mai magia).
 */
export async function opfsDataBytes(): Promise<number | null> {
  try {
    const root = await navigator.storage.getDirectory();
    let total = 0;
    // TS lib.dom non tipizza gli iteratori asincroni degli handle OPFS (l'API li ha):
    // cast locale tipizzato, verificato dal probe su WebKit reale (scripts/probe-m0-diagnostics.mjs)
    type DirIterable = FileSystemDirectoryHandle & { values(): AsyncIterableIterator<FileSystemHandle> };
    const walk = async (dir: FileSystemDirectoryHandle): Promise<void> => {
      for await (const entry of (dir as DirIterable).values()) {
        if (entry.kind === "directory") await walk(entry as FileSystemDirectoryHandle);
        else total += (await (entry as FileSystemFileHandle).getFile()).size;
      }
    };
    await walk(root);
    return total;
  } catch {
    return null;
  }
}
